"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ImageMediaModal, requestCenteredImageFullscreen } from "@/components/drops/view/item/content/media/ImageMediaModal";
import { useMediaActions } from "@/components/drops/view/item/content/media/useMediaActions";
import useCapacitor from "@/hooks/useCapacitor";
import type { DropImageGalleryItem } from "./dropImageGallery";
interface DropImageGalleryContextValue {
  readonly openImage: (galleryItemId: string) => boolean;
}
const DropImageGalleryContext = createContext<DropImageGalleryContextValue | null>(null);
export const useDropImageGallery = () => {
  return useContext(DropImageGalleryContext);
};
export function DropImageGalleryProvider(t0) {
  const $ = _c(28);
  const {
    children,
    items
  } = t0;
  const [activeItemId, setActiveItemId] = useState(null);
  let t1;
  if ($[0] !== activeItemId || $[1] !== items) {
    t1 = activeItemId ? items.findIndex(item => item.id === activeItemId) : -1;
    $[0] = activeItemId;
    $[1] = items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const activeIndex = t1;
  const activeItem = activeIndex >= 0 ? items[activeIndex] : undefined;
  const canGoPrevious = activeIndex > 0;
  const canGoNext = activeIndex >= 0 && activeIndex + 1 < items.length;
  let t2;
  let t3;
  if ($[3] !== activeItemId || $[4] !== items) {
    t2 = () => {
      if (!activeItemId) {
        return;
      }
      if (!items.some(item_0 => item_0.id === activeItemId)) {
        setActiveItemId(null);
      }
    };
    t3 = [activeItemId, items];
    $[3] = activeItemId;
    $[4] = items;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== items) {
    t4 = galleryItemId => {
      const item_1 = items.find(galleryItem => galleryItem.id === galleryItemId);
      if (!item_1) {
        return false;
      }
      setActiveItemId(item_1.id);
      return true;
    };
    $[7] = items;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const openImage = t4;
  let t5;
  if ($[9] !== items) {
    t5 = () => {
      setActiveItemId(currentId => {
        const currentIndex = items.findIndex(item_2 => item_2.id === currentId);
        if (currentIndex <= 0) {
          return currentId;
        }
        return items[currentIndex - 1]?.id ?? currentId;
      });
    };
    $[9] = items;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const goToPrevious = t5;
  let t6;
  if ($[11] !== items) {
    t6 = () => {
      setActiveItemId(currentId_0 => {
        const currentIndex_0 = items.findIndex(item_3 => item_3.id === currentId_0);
        if (currentIndex_0 < 0 || currentIndex_0 + 1 >= items.length) {
          return currentId_0;
        }
        return items[currentIndex_0 + 1]?.id ?? currentId_0;
      });
    };
    $[11] = items;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const goToNext = t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setActiveItemId(null);
    };
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleClose = t7;
  let t8;
  if ($[14] !== openImage) {
    t8 = {
      openImage
    };
    $[14] = openImage;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const value = t8;
  let t9;
  if ($[16] !== activeIndex || $[17] !== activeItem || $[18] !== canGoNext || $[19] !== canGoPrevious || $[20] !== goToNext || $[21] !== goToPrevious || $[22] !== items) {
    t9 = activeItem && <DropImageGalleryModal activeIndex={activeIndex} activeItem={activeItem} canGoNext={canGoNext} canGoPrevious={canGoPrevious} onClose={handleClose} onNext={goToNext} onPrevious={goToPrevious} total={items.length} />;
    $[16] = activeIndex;
    $[17] = activeItem;
    $[18] = canGoNext;
    $[19] = canGoPrevious;
    $[20] = goToNext;
    $[21] = goToPrevious;
    $[22] = items;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== children || $[25] !== t9 || $[26] !== value) {
    t10 = <DropImageGalleryContext.Provider value={value}>{children}{t9}</DropImageGalleryContext.Provider>;
    $[24] = children;
    $[25] = t9;
    $[26] = value;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
function DropImageGalleryModal(t0) {
  const $ = _c(19);
  const {
    activeIndex,
    activeItem,
    canGoNext,
    canGoPrevious,
    onClose,
    onNext,
    onPrevious,
    total
  } = t0;
  const modalImageRef = useRef(null);
  const {
    isCapacitor
  } = useCapacitor();
  let t1;
  if ($[0] !== activeItem.src) {
    t1 = {
      url: activeItem.src,
      fallbackFileName: "image",
      dialogTitle: "Save image",
      mimeType: "image"
    };
    $[0] = activeItem.src;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    downloadMedia,
    isDownloading,
    openLabel,
    openMedia
  } = useMediaActions(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const fullscreenTarget = modalImageRef.current;
      if (fullscreenTarget) {
        requestCenteredImageFullscreen(fullscreenTarget);
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleFullScreen = t2;
  const t3 = !isCapacitor;
  let t4;
  if ($[3] !== activeIndex || $[4] !== canGoNext || $[5] !== canGoPrevious || $[6] !== onNext || $[7] !== onPrevious || $[8] !== total) {
    t4 = {
      canGoNext,
      canGoPrevious,
      currentIndex: activeIndex,
      onNext,
      onPrevious,
      total
    };
    $[3] = activeIndex;
    $[4] = canGoNext;
    $[5] = canGoPrevious;
    $[6] = onNext;
    $[7] = onPrevious;
    $[8] = total;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== activeItem.src || $[11] !== downloadMedia || $[12] !== isDownloading || $[13] !== onClose || $[14] !== openLabel || $[15] !== openMedia || $[16] !== t3 || $[17] !== t4) {
    t5 = <ImageMediaModal src={activeItem.src} imageRef={modalImageRef} onClose={onClose} onOpen={openMedia} openLabel={openLabel} onDownload={downloadMedia} isDownloading={isDownloading} onFullscreen={handleFullScreen} fullscreenTargetAvailable={t3} gallery={t4} />;
    $[10] = activeItem.src;
    $[11] = downloadMedia;
    $[12] = isDownloading;
    $[13] = onClose;
    $[14] = openLabel;
    $[15] = openMedia;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}